// Settings → Plan: the current plan, what it includes, and the way to change it.
// Billing (Stripe portal) only shows for subscriptions that actually go through Stripe.
import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../../../firebase';
import PlanPurchasePage from '../../../myPlans/PlanPurchasePage';
import PaymentSetting from './paymentSetting';
import { FREE_MESSAGE_LIMIT, PRO_MESSAGE_LIMIT } from '../../../myWorkspace/workspaceComponents/chat/chatModel';
import './selectedPlan.css';

const formatDate = d => d?.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) || '—';

const SelectedPlan = () => {
  const user = auth.currentUser;
  const [plan, setPlan] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [renewing, setRenewing] = useState(false);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'users', user.uid, 'subscriptionData', 'planDetails'), snap => setPlan(snap.data() || {}), () => {});
  }, [user]);

  if (!plan) return null;

  const isPro = (plan.currentPlan || '').toLowerCase() === 'pro';
  const hasSubscription = isPro && !!plan.paymentInterval;
  const isCancelling = hasSubscription && !!plan.cancelAtPeriodEnd;
  const minutes = (plan.creditsLimit || 0).toLocaleString();

  // Cancelled subscriptions are renewed from the Stripe billing portal
  const handleRenew = async () => {
    setRenewing(true);
    try {
      const { data } = await httpsCallable(getFunctions(), 'manageBilling')();
      window.location.href = data.url;
    } catch {
      setRenewing(false);
    }
  };

  const benefits = isPro
    ? ['Everything in Free', `${minutes} minutes of usage per month`, 'Upload your own videos, up to 16 GB', 'Uploads up to 3h 30m long', `Chat with your video, ${PRO_MESSAGE_LIMIT} messages each`, 'Creator Studio: publish and schedule to YouTube', 'Description templates', 'API access for YouTube links']
    : [`${minutes} minutes of usage per month`, 'Any public YouTube video, up to 3 hours', 'AI timestamps for every video', `Chat with your video, ${FREE_MESSAGE_LIMIT} messages each`, 'Answers in 11 languages'];

  return (
    <>
      <div className="setting-header">Current plan</div>
      <div className="account-settings-container">
        <div className="settings-row">
          <div className="settings-label plan-name">
            {isPro ? (hasSubscription ? 'Bumpups Pro' : 'Bumpups Pro · Custom') : 'Free Plan'}
          </div>
          <div className="plan-settings-field">
            {isCancelling && (
              <button type="button" className="ui-setting-button" onClick={handleRenew} disabled={renewing}>
                {renewing ? 'Opening…' : 'Renew'}
              </button>
            )}
            {(!isPro || hasSubscription) && (
              <button
                type="button"
                className={`ui-setting-button${isPro ? '' : ' ui-setting-button--primary'}`}
                onClick={() => setShowPlans(true)}
              >
                {isPro ? 'Change plan' : 'Upgrade'}
              </button>
            )}
          </div>
        </div>

        <div className={`settings-subtitle-plan${isCancelling ? ' subtitle-canceled' : ''}`}>
          {isCancelling ? (
            <>Your subscription ends on <strong>{formatDate(plan.billingCycleEnd?.toDate())}</strong>, and you’ll be moved back to the Free Plan.</>
          ) : (
            <><strong>Usage resets:</strong> {formatDate(plan.quotaCycleEnd?.toDate())}</>
          )}
        </div>

        <div className="plan-details-box">
          <p className="plan-intro">
            {!isPro
              ? 'You’re on our Free Plan — here’s what you get:'
              : hasSubscription
                ? 'Thanks for subscribing to Bumpups Pro! Your plan includes:'
                : 'Your Pro plan was set up by the Bumpups team and includes:'}
          </p>
          <ul className="plan-benefits">
            {benefits.map(item => <li key={item}>{item}</li>)}
          </ul>
        </div>
      </div>

      {hasSubscription && <PaymentSetting />}
      {showPlans && <PlanPurchasePage source="settings" onClose={() => setShowPlans(false)} />}
    </>
  );
};

export default SelectedPlan;
